/**
 * LabelingPanel - Right sidebar for manual labeling
 *
 * Features:
 * - Nav buttons in header (always visible)
 * - Keyboard shortcuts: P for Present, A for Absent
 * - Auto-advance to next case when complete
 * - Progress indicator with case completion status
 * - Shows existing labels with who labeled and when
 */

import { useEffect, useRef, useCallback, useState, useMemo } from "react";
import { useAtom, useAtomValue } from "jotai";
import { labelMapAtom, LabelInfo, type ExamDisposition } from "../../atoms/labelingAtoms";
import { userIdAtom, tokenAtom } from "../../atoms/authAtoms";
import { authService } from "../../services/auth";
import { decodeToken } from "../../utils/tokenUtils";
import { logger } from "../../utils/logger";
import {
  useLabelingQueries,
  useCaseLabelsQuery,
  useMarkCompleteMutation,
  useUnmarkCompleteMutation,
  useCasesQuery,
} from "../../hooks/useLabelingQueries";
import {
  FaCheck,
  FaTimes,
  FaLock,
  FaChevronLeft,
  FaChevronRight,
  FaFileAlt,
} from "../../utils/icons";
import { WorkListButton } from "./WorkListButton";
import { InstructionsDialog } from "./InstructionsDialog";
import {
  AttributeCardinality,
  AttributeDefinitions,
  MULTI_SELECT_ATTRIBUTES,
  buildFindingValue,
  isFindingComplete,
  parseAttributes,
  parseCall,
  parseFindingValue,
} from "./findingAttributeValue";
import { UnassessableCaseControl } from "./UnassessableCaseControl";

interface LabelingPanelProps {
  projectId: string;
  readOnly?: boolean;
  caseId: string;
  studyInstanceUid: string;
  labelsToAssign: string[];
  onNext: () => void;
  onPrevious: () => void;
  onNextUnlabeled?: () => void;
  onWorkList?: () => void;
  hasNext: boolean;
  hasPrevious: boolean;
  hasNextUnlabeled?: boolean;
  currentIndex: number;
  totalCases: number;
  /** Worklist's `instructions_key` for authenticated project instructions. */
  instructionsKey?: string | null;
  /**
   * Classification label options from the worklist's `label_schema.options`.
   * Defaults to ["Present","Absent"] when absent so existing binary projects are
   * unchanged. A 3rd+ option (e.g. "Cannot be assessed") renders an extra button.
   */
  labelOptions?: string[] | null;
  /**
   * Per-label option override from `label_schema.options_by_label` (e.g. a study-level grade read
   * alongside the findings). Labels not listed use `labelOptions`. A label whose options exclude
   * "Absent" is not touched by "mark rest Absent".
   */
  optionsByLabel?: Record<string, string[]> | null;
  /** Optional button abbreviation per option (`label_schema.option_abbreviations`). */
  optionAbbreviations?: Record<string, string> | null;
  /**
   * ONE-ROUND combined mode (worklist `single_select_with_attributes`). When
   * provided, a Present call reveals + requires that finding's attributes inline
   * in the same read; the finding's single row stores them as JSON. ABSENT ⇒
   * the panel is byte-identical to the plain single_select path (chest/renal/omni).
   */
  attributeDefinitions?: AttributeDefinitions;
  /** Explicit per-finding attribute cardinality supplied by the worklist. */
  attributeCardinality?: AttributeCardinality;
  /** Worklist exam-disposition policy: Exclude and Technical problem replace Mark unassessable. */
  examDisposition?: ExamDisposition;
}

/**
 * Reader-facing reason a write was rejected. Prefers the backend's own detail (e.g. "Cannot
 * modify label set by …" on a 403 ownership conflict) so a real conflict reads as a conflict
 * rather than a generic failure.
 */
function saveErrorMessage(error: unknown): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim() !== "") return detail;
  return "Not saved — your answer was not recorded. Check your connection and try again.";
}

/**
 * Format user email for display
 * Maps known emails to friendly names, otherwise shows first part of email
 */
function formatLabeledBy(email: string): string {
  // Known user mappings
  const knownUsers: Record<string, string> = {
    "michael@a2zradiology.ai": "Michael",
    admin: "Michael", // Legacy "admin" entries = Michael
  };

  if (knownUsers[email]) {
    return knownUsers[email];
  }

  // Extract first part of email before @
  const atIndex = email.indexOf("@");
  if (atIndex > 0) {
    const name = email.substring(0, atIndex);
    // Capitalize first letter
    return name.charAt(0).toUpperCase() + name.slice(1);
  }

  return email;
}

export function LabelingPanel({
  caseId,
  studyInstanceUid,
  projectId,
  labelsToAssign,
  onNext,
  onPrevious,
  onNextUnlabeled,
  onWorkList,
  hasNext,
  hasPrevious,
  hasNextUnlabeled,
  currentIndex,
  totalCases,
  instructionsKey,
  optionsByLabel,
  optionAbbreviations,
  readOnly = false,
  labelOptions,
  attributeDefinitions,
  attributeCardinality,
  examDisposition,
}: LabelingPanelProps) {
  // Combined finding+attribute mode is active iff the worklist supplied
  // attribute_definitions. Everything new below is gated on this so the plain
  // single_select path (chest/renal/omni) is provably unchanged.
  const combined = !!attributeDefinitions;
  // Render label buttons from the worklist schema; fall back to binary Present/Absent so existing
  // projects (omni/abdo) are byte-for-byte unchanged. Each option gets a keyboard shortcut = its first
  // letter (Present→p, Absent→a, "Cannot be assessed"→c); on a first-letter collision only the first wins.
  const OPTIONS: string[] =
    labelOptions && labelOptions.length >= 2 ? labelOptions : ["Present", "Absent"];
  // Per-label override (study-level grade etc.); every other label uses OPTIONS.
  const optionsFor = (label: string): string[] => {
    const o = optionsByLabel?.[label];
    return o && o.length >= 2 ? o : OPTIONS;
  };
  const keyMapFor = (label: string): Record<string, string> => {
    const m: Record<string, string> = {};
    for (const opt of optionsFor(label)) {
      const k = opt.trim()[0]?.toLowerCase() ?? "";
      if (k && !(k in m)) m[k] = opt;
    }
    return m;
  };
  const optionAbbrev = (opt: string): string =>
    optionAbbreviations?.[opt] ?? (opt === "Cannot be assessed" ? "N/A" : opt.trim().slice(0, 3));
  const [showInstructions, setShowInstructions] = useState(false);
  // Last rejected write, surfaced inline. null = nothing to report.
  const [saveError, setSaveError] = useState<string | null>(null);
  const [labelMap, setLabelMap] = useAtom(labelMapAtom);
  const currentUserId = useAtomValue(userIdAtom);
  // The backend stamps `labeled_by` from the token's EMAIL claim, which can differ from
  // `userIdAtom` depending on the auth path. Derive the email so ownership matches what was
  // actually stored — otherwise a user's own saved labels read as "someone else's" and lock.
  //
  // Read the token the SAME way the request that writes the label does: the axios interceptor
  // (`AuthContext.tsx:124`) takes it from the cookie via `authService.getStoredAuth()`, while
  // `authStateAtom = atom(getInitialAuthState())` only snapshots cookies ONCE at module import.
  // When those two diverge the save still succeeds under the real user — the backend stamped the
  // correct email on every row in #866 — while this panel has no identity at all, or a stale
  // token bearing a DIFFERENT email. Resolving from the cookie keeps ownership evaluable in that
  // window, so the fail-open guard below stays a genuine last resort instead of the normal path:
  // a session that falls open has no ownership protection at all until it reloads.
  // Note: `atomToken` is the only dep that can change — a cookie rotation mid-session
  // won't recompute this. Benign today (a stale email only affects which rows read as
  // yours until the next render that does change atomToken), but don't build on the
  // assumption that this tracks the cookie live.
  const atomToken = useAtomValue(tokenAtom);
  const identity = useMemo(() => {
    const token = authService.getStoredAuth().token ?? atomToken;
    if (!token) return { email: null, reason: "no_token" as const };
    const email = decodeToken(token)?.email ?? null;
    return { email, reason: email == null ? ("token_without_email" as const) : null };
  }, [atomToken]);
  const currentUserEmail = identity.email;
  // Log the unresolved-identity cases as an effect, not inside the memo: a side effect
  // in useMemo double-fires under StrictMode and re-fires on every recompute. Both
  // branches are reported — `no_token` is the most-cited #866 trigger and was the
  // silent one, which made a fail-open indistinguishable from a normal render.
  //
  // A token we cannot extract an email from is the failure behind three reader-visible
  // lockouts (#818, the 2026-07-24 mark-complete report, #866), each with clean stored
  // data and NO log to diagnose from — decodeToken swallows parse errors in a bare
  // catch (#862), so this is the first point that can say anything.
  const reportedIdentityReason = useRef<string | null>(null);
  useEffect(() => {
    if (!identity.reason || reportedIdentityReason.current === identity.reason) return;
    reportedIdentityReason.current = identity.reason;
    logger.warn(
      `labeling_identity_unresolved: ${identity.reason}; ownership checks will fail open`,
    );
  }, [identity.reason]);
  const { saveLabelMutation, deleteLabelMutation, progressQuery } = useLabelingQueries(projectId);
  const markCompleteMutation = useMarkCompleteMutation(projectId, studyInstanceUid);
  const unmarkCompleteMutation = useUnmarkCompleteMutation(projectId, studyInstanceUid);
  const casesQuery = useCasesQuery(projectId);
  const currentCaseStatus = casesQuery.data?.cases.find(
    (item) => item.study_instance_uid === studyInstanceUid.split("::")[0],
  );
  const isCaseUnassessable = currentCaseStatus?.completion_status === "unassessable";
  const isExamExcluded = currentCaseStatus?.exam_excluded === true;
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const completeTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const autoAdvancedRef = useRef(false);
  const loadedCasesRef = useRef<Set<string>>(new Set());
  // null = no explicit focus; falls back to first-unlabeled for P/A targeting
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  // Fetch existing labels for current case. `isSuccess` / `isError` gate the
  // bulk-Absent quick action below: `caseLabels` is empty both before this
  // resolves and when a case genuinely has no labels, and the two must not be
  // confused by anything that writes in bulk.
  const {
    data: existingLabels,
    isSuccess: labelsLoaded,
    isError: labelsFailed,
    isFetching: labelsFetching,
  } = useCaseLabelsQuery(projectId, caseId);

  // Pre-populate labelMap with existing labels when loaded
  useEffect(() => {
    if (existingLabels?.labels && existingLabels.labels.length > 0) {
      // Only load once per case to avoid overwriting user's current session changes
      if (loadedCasesRef.current.has(caseId)) {
        return;
      }
      loadedCasesRef.current.add(caseId);

      setLabelMap((prev) => {
        const existingCaseLabels = prev[caseId] || {};
        const newLabels: Record<string, LabelInfo> = {};

        for (const label of existingLabels.labels) {
          // Don't overwrite if already in local state (user might have just labeled)
          if (!existingCaseLabels[label.label_key]) {
            newLabels[label.label_key] = {
              value: label.label_value,
              labeled_by: label.labeled_by,
              labeled_at: label.labeled_at,
            };
          }
        }

        // Only update if we have new labels to add
        if (Object.keys(newLabels).length === 0) {
          return prev;
        }

        return {
          ...prev,
          [caseId]: {
            ...existingCaseLabels,
            ...newLabels,
          },
        };
      });
    }
  }, [existingLabels, caseId, setLabelMap]);

  // Count complete findings for the current case. In combined mode a finding is
  // "complete" only when it has a call AND (if Present) all its attributes are
  // filled; in single_select mode any recorded call completes it — so this
  // equals the legacy Object.keys(caseLabels).length for the chest/renal path.
  const caseLabels = labelMap[caseId] || {};
  const totalConditions = labelsToAssign.length;
  const labeledCount = labelsToAssign.filter((c) =>
    isFindingComplete(caseLabels[c]?.value, attributeDefinitions?.[c], combined),
  ).length;
  const isCaseComplete = labeledCount === totalConditions && totalConditions > 0;

  // Find first condition with no call yet, for keyboard focus.
  const firstUnlabeledCondition = labelsToAssign.find((c) => !caseLabels[c]);

  // Check if current user owns a label (can only modify your own). Match on the email the
  // backend stores (`labeled_by`), falling back to userId for legacy/non-JWT sessions. Matching
  // userId alone locked users out of editing their own prior answers when the auth path made
  // userId != email (the backend still enforces true ownership on save, so this only gates the UI).
  const isOwnLabel = useCallback(
    (labelInfo: LabelInfo | undefined) => {
      if (!labelInfo) return true; // No label yet, anyone can set
      const owner = labelInfo.labeled_by;
      // FAIL OPEN when we cannot resolve our own email. `labeled_by` is always the
      // backend-stamped email claim, so a null email means "ownership is not evaluable
      // here" -- NOT "this belongs to someone else". Locking in that state is what broke
      // #866: a reader's own freshly-saved label came back email-stamped, the client could
      // not resolve its own email, and every call button plus the mandatory attribute
      // buttons went disabled mid-batch. The backend enforces true ownership on save, so a
      // client-side identity gap must never be the thing that blocks a radiologist.
      // The userId clause below cannot be relied on to rescue this. It DOES match on the normal
      // login path — the backend returns `user_id=email` (`login_endpoints.py:583`) and mints a
      // JWT with `sub` and `email` both set to it — but not on every path (service-account login
      // returns `user_id="service-account"`), and nothing keeps a stored userId in step with the
      // email the backend stamps. So a null email still has to fail open on its own.
      if (currentUserEmail == null) return true;
      // Email compared case-insensitively — the backend may normalize case while the JWT carries
      // mixed case; an exact compare would silently revert to the locked behavior.
      return owner?.toLowerCase() === currentUserEmail.toLowerCase() || owner === currentUserId;
    },
    [currentUserId, currentUserEmail],
  );

  // Put a finding's row back to what it was before an optimistic write the backend then
  // rejected. Local state must never claim an answer the server refused — on a reader study that
  // silently diverges recorded ground truth from what the radiologist believes they signed. This
  // is the other half of failing open above: the UI now permits a click it cannot prove is ours,
  // so the 403 that comes back has to be visible rather than swallowed.
  // Roll back a rejected optimistic write — but ONLY if our own optimistic value
  // is still the one on screen. `written` is what this call optimistically set
  // (undefined for a toggle-off, whose optimistic state is absence).
  //
  // Buttons are not disabled while a save is in flight, so a reader can label the
  // same finding twice inside one save window — ordinary P/A power-labeling. An
  // unconditional revert to the click-time snapshot would then undo the SECOND
  // write, which the server accepted, and the divergence survives the session:
  // useSaveLabelMutation invalidates only ["progress"], never ["case-labels"], and
  // the hydration effect skips keys already in local state, so late-arriving
  // server truth is discarded in favour of the wrong local value.
  const revertLabel = useCallback(
    (labelKey: string, previous: LabelInfo | undefined, written: string | undefined) => {
      setLabelMap((prev) => {
        const current = prev[caseId]?.[labelKey];
        // A later write already superseded ours — it is the server's truth now,
        // so leave it alone. Reverting here is what erased accepted writes.
        if (current?.value !== written) return prev;
        const updated = { ...prev[caseId] };
        if (previous) updated[labelKey] = previous;
        else delete updated[labelKey];
        return { ...prev, [caseId]: updated };
      });
    },
    [caseId, setLabelMap],
  );

  const handleLabelSelect = useCallback(
    async (labelKey: string, call: string) => {
      const existing = caseLabels[labelKey];

      // Block modifying someone else's label
      if (existing && !isOwnLabel(existing)) return;
      setSaveError(null);

      // Toggle off: clicking the current call clears the label (and, in combined
      // mode, its inline attributes along with it). parseCall handles both the
      // plain-string single_select value and the Present JSON.
      const existingCall = existing ? parseCall(existing.value) : undefined;
      if (existingCall === call) {
        setLabelMap((prev) => {
          const updated = { ...prev[caseId] };
          delete updated[labelKey];
          return { ...prev, [caseId]: updated };
        });
        try {
          await deleteLabelMutation.mutateAsync({
            project_id: projectId,
            case_id: caseId,
            label_key: labelKey,
          });
        } catch (error) {
          console.error("Failed to delete label:", error);
          revertLabel(labelKey, existing, undefined);
          setSaveError(saveErrorMessage(error));
        }
        return;
      }

      // Switching into Present starts with no attributes; Absent / Cannot-be-
      // assessed store the plain call string (attributes are N-A). In
      // single_select mode buildFindingValue returns the call verbatim.
      const storedValue = buildFindingValue(call, {}, combined);
      setLabelMap((prev) => ({
        ...prev,
        [caseId]: {
          ...prev[caseId],
          [labelKey]: {
            value: storedValue,
            labeled_by: currentUserEmail ?? currentUserId ?? "unknown",
            labeled_at: new Date().toISOString(),
          },
        },
      }));

      try {
        await saveLabelMutation.mutateAsync({
          project_id: projectId,
          case_id: caseId,
          study_instance_uid: studyInstanceUid,
          label_key: labelKey,
          label_value: storedValue,
          notes: "",
        });
      } catch (error) {
        console.error("Failed to save label:", error);
        revertLabel(labelKey, existing, storedValue);
        setSaveError(saveErrorMessage(error));
      }
    },
    [
      caseId,
      caseLabels,
      studyInstanceUid,
      currentUserId,
      isOwnLabel,
      setLabelMap,
      saveLabelMutation,
      deleteLabelMutation,
      revertLabel,
      projectId,
      combined,
    ],
  );

  // Toggle one attribute option for a Present finding (combined mode only).
  // Persists the finding's single row with the updated attributes bundled in.
  const handleAttrToggle = useCallback(
    async (labelKey: string, attrName: string, option: string) => {
      const existing = caseLabels[labelKey];
      if (!existing || parseCall(existing.value) !== "Present") return;
      if (!isOwnLabel(existing)) return;
      setSaveError(null);

      const attrs = parseAttributes(existing.value);
      const cur = attrs[attrName] ?? [];
      // Select-one unless the dimension is genuinely multi-mark: REPLACE rather than append. Clicking an
      // already-selected option still clears it, in both modes. Replacing also self-heals a cell holding a
      // value that has since been removed from the worklist's option list — it renders as nothing selected,
      // and appending would leave the stale value invisibly behind it (37 Midline Shift cells are in that
      // state after "Less than 5mm" was retired).
      const configuredCardinality = attributeCardinality?.[labelKey]?.[attrName];
      const multi =
        configuredCardinality === "multi_select" ||
        (configuredCardinality == null && MULTI_SELECT_ATTRIBUTES.has(attrName));
      const nextSel = cur.includes(option)
        ? cur.filter((v) => v !== option)
        : multi
          ? [...cur, option]
          : [option];
      const storedValue = buildFindingValue("Present", { ...attrs, [attrName]: nextSel }, true);

      setLabelMap((prev) => ({
        ...prev,
        [caseId]: {
          ...prev[caseId],
          [labelKey]: {
            value: storedValue,
            labeled_by: currentUserEmail ?? currentUserId ?? "unknown",
            labeled_at: new Date().toISOString(),
          },
        },
      }));

      try {
        await saveLabelMutation.mutateAsync({
          project_id: projectId,
          case_id: caseId,
          study_instance_uid: studyInstanceUid,
          label_key: labelKey,
          label_value: storedValue,
          notes: "",
        });
      } catch (error) {
        console.error("Failed to save attribute:", error);
        revertLabel(labelKey, existing, storedValue);
        setSaveError(saveErrorMessage(error));
      }
    },
    [
      caseId,
      caseLabels,
      studyInstanceUid,
      currentUserId,
      isOwnLabel,
      setLabelMap,
      saveLabelMutation,
      revertLabel,
      projectId,
      attributeCardinality,
    ],
  );

  // Target condition for P/A: explicit arrow-key focus wins; otherwise the
  // first unlabeled condition (power-labeling default). Falls back to row 0
  // when every condition is labeled so keyboard shortcuts keep working.
  const firstUnlabeledIndex = firstUnlabeledCondition
    ? labelsToAssign.indexOf(firstUnlabeledCondition)
    : -1;
  const effectiveFocusIndex =
    focusedIndex !== null ? focusedIndex : Math.max(0, firstUnlabeledIndex);
  const focusedCondition =
    effectiveFocusIndex >= 0 ? labelsToAssign[effectiveFocusIndex] : undefined;

  // Keyboard shortcuts:
  //   ↑ / k  move focus up
  //   ↓ / j  move focus down
  //   P      Present on focused condition
  //   A      Absent on focused condition
  // Bind the window listener ONCE and dispatch through a ref that always holds
  // the latest closure. Re-binding on every render (the previous deps —
  // `handleLabelSelect` rebuilds when labels load, `focusedCondition` changes
  // per keypress) opened a removeEventListener→addEventListener gap in React's
  // passive-effect phase; under the unified viewport path's re-render storm
  // (#575) that gap widened enough to drop dispatched ArrowDown / P / A keys
  // (#576). A stable listener can't drop keys regardless of re-render rate.
  const keyHandlerRef = useRef<(e: KeyboardEvent) => void>(() => {});
  keyHandlerRef.current = (e: KeyboardEvent) => {
    if (readOnly || isExamExcluded) return;
    if (
      e.target instanceof HTMLInputElement ||
      e.target instanceof HTMLTextAreaElement ||
      e.target instanceof HTMLSelectElement
    ) {
      return;
    }

    const total = labelsToAssign.length;
    if (total === 0) return;

    const isUp = e.key === "ArrowUp" || e.key === "k";
    const isDown = e.key === "ArrowDown" || e.key === "j";
    if (isUp || isDown) {
      e.preventDefault();
      setFocusedIndex((prev) => {
        const start = prev !== null ? prev : Math.max(0, firstUnlabeledIndex);
        const next = isUp ? start - 1 : start + 1;
        return Math.max(0, Math.min(total - 1, next));
      });
      return;
    }

    if (!focusedCondition) return;

    const opt = keyMapFor(focusedCondition)[e.key.toLowerCase()];
    if (opt) {
      e.preventDefault();
      handleLabelSelect(focusedCondition, opt);
    }
  };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => keyHandlerRef.current(e);
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // Reset explicit focus when the case changes so P/A power-labeling resumes
  useEffect(() => {
    setFocusedIndex(null);
    setSaveError(null);
  }, [caseId]);

  // Auto-advance to next unlabeled case when complete - DISABLED
  // useEffect(() => {
  //   // Only auto-advance if there's somewhere to go
  //   const canAdvance = hasNextUnlabeled || hasNext;
  //   if (isCaseComplete && canAdvance && !autoAdvancedRef.current) {
  //     autoAdvancedRef.current = true;
  //     // Small delay so user sees the completion state
  //     const timer = setTimeout(() => {
  //       // Prefer next unlabeled, fall back to sequential next
  //       if (hasNextUnlabeled && onNextUnlabeled) {
  //         onNextUnlabeled();
  //       } else {
  //         onNext();
  //       }
  //     }, 800);
  //     return () => clearTimeout(timer);
  //   }
  // }, [isCaseComplete, hasNext, hasNextUnlabeled, onNext, onNextUnlabeled]);

  // Reset state when case changes
  useEffect(() => {
    autoAdvancedRef.current = false;
    setShowCompleteConfirm(false);
    clearTimeout(completeTimerRef.current);
  }, [caseId]);

  // Cleanup timer on unmount
  useEffect(() => () => clearTimeout(completeTimerRef.current), []);

  const handleAdvance = useCallback(() => {
    if (hasNextUnlabeled && onNextUnlabeled) {
      onNextUnlabeled();
    } else if (hasNext) {
      onNext();
    }
  }, [hasNextUnlabeled, onNextUnlabeled, hasNext, onNext]);

  const handleMarkComplete = useCallback(() => {
    markCompleteMutation.mutate(undefined, {
      onSuccess: () => {
        setShowCompleteConfirm(true);
        completeTimerRef.current = setTimeout(() => {
          setShowCompleteConfirm(false);
          handleAdvance();
        }, 1500);
      },
    });
  }, [markCompleteMutation, handleAdvance]);

  const handleUndoComplete = useCallback(() => {
    clearTimeout(completeTimerRef.current);
    setShowCompleteConfirm(false);
    unmarkCompleteMutation.mutate();
  }, [unmarkCompleteMutation]);

  // Findings with no call yet — target of the "mark rest Absent" quick action.
  // A label whose options exclude "Absent" (e.g. a study-level grade) is never bulk-filled.
  const unlabeledConditions = labelsToAssign.filter(
    (c) => !caseLabels[c] && optionsFor(c).includes("Absent"),
  );

  // True while the bulk loop is writing. Mark Complete is disabled on it: the
  // optimistic map already counts the bulk rows toward isCaseComplete, so
  // without this a case could be marked complete while rows are still unwritten
  // (or have failed) server-side.
  const [bulkSaving, setBulkSaving] = useState(false);
  // Keys the bulk action wrote, so it can be undone. null = nothing to undo.
  const [bulkUndo, setBulkUndo] = useState<string[] | null>(null);

  // Bulk-set every still-unlabeled finding to Absent (readers' common case is
  // "almost all absent"). Only fills findings with NO call — never clobbers an
  // existing Present / Absent / Cannot-be-assessed.
  //
  // Gated on `labelsLoaded` at the call site: before the fetch resolves,
  // caseLabels is empty and EVERY finding looks unlabeled, so this would offer
  // to Absent a case the reader had already filled in. That clobber would also
  // be permanent — the hydration effect above loads once per case and skips keys
  // already in local state, so the server's real values are discarded in favour
  // of the bulk write and the UI never shows a disagreement to re-check.
  const handleMarkRestAbsent = useCallback(async () => {
    const targets = unlabeledConditions;
    if (targets.length === 0 || !labelsLoaded) return;
    setSaveError(null);
    setBulkUndo(null);
    setBulkSaving(true);
    const storedValue = buildFindingValue("Absent", {}, combined);
    const by = currentUserEmail ?? currentUserId ?? "unknown";
    const at = new Date().toISOString();
    setLabelMap((prev) => {
      const cur = { ...prev[caseId] };
      for (const c of targets) cur[c] = { value: storedValue, labeled_by: by, labeled_at: at };
      return { ...prev, [caseId]: cur };
    });

    const failed: string[] = [];
    let lastError: unknown = null;
    for (const c of targets) {
      try {
        await saveLabelMutation.mutateAsync({
          project_id: projectId,
          case_id: caseId,
          study_instance_uid: studyInstanceUid,
          label_key: c,
          label_value: storedValue,
          notes: "",
        });
      } catch (error) {
        console.error(`Failed to save Absent for ${c}:`, error);
        failed.push(c);
        lastError = error;
      }
    }

    if (failed.length > 0) {
      // Drop the optimistic entries the server didn't accept, so labeledCount
      // stops counting them and the reader sees the gap instead of a complete
      // case. Only remove OUR value — a concurrent single-click write wins.
      setLabelMap((prev) => {
        const cur = { ...prev[caseId] };
        for (const c of failed) if (cur[c]?.value === storedValue) delete cur[c];
        return { ...prev, [caseId]: cur };
      });
      setSaveError(
        `${failed.length} of ${targets.length} not saved (${failed.join(", ")}). ` +
          `${saveErrorMessage(lastError)}`,
      );
    }

    const written = targets.filter((c) => !failed.includes(c));
    setBulkUndo(written.length > 0 ? written : null);
    setBulkSaving(false);
  }, [
    unlabeledConditions,
    labelsLoaded,
    combined,
    currentUserEmail,
    currentUserId,
    setLabelMap,
    caseId,
    saveLabelMutation,
    projectId,
    studyInstanceUid,
  ]);

  // Undo the bulk write: an unlabeled→Absent sweep is otherwise irreversible by
  // hand (toggling Absent off deletes the row, and there were N of them).
  const handleUndoMarkRestAbsent = useCallback(async () => {
    const keys = bulkUndo;
    if (!keys || keys.length === 0) return;
    setBulkUndo(null);
    const storedValue = buildFindingValue("Absent", {}, combined);
    // Undo only the rows that are STILL our bulk-written Absent. A finding the
    // reader has called Present since the sweep is deliberate work, and deleting
    // its row would destroy the call and its attributes unrecoverably. Same
    // supersede check the bulk failure path and revertLabel already make.
    const undoable = keys.filter((c) => caseLabels[c]?.value === storedValue);
    const superseded = keys.length - undoable.length;
    if (superseded > 0) {
      logger.warn(
        `labeling_bulk_undo_skipped_superseded: left ${superseded} of ${keys.length} ` +
          `findings alone, re-called after the bulk write`,
      );
    }
    if (undoable.length === 0) {
      setSaveError(
        "Nothing to undo: every finding from that sweep has since been re-called by hand.",
      );
      return;
    }
    setBulkSaving(true);
    setLabelMap((prev) => {
      const cur = { ...prev[caseId] };
      for (const c of undoable) if (cur[c]?.value === storedValue) delete cur[c];
      return { ...prev, [caseId]: cur };
    });
    const failed: string[] = [];
    for (const c of undoable) {
      try {
        await deleteLabelMutation.mutateAsync({
          project_id: projectId,
          case_id: caseId,
          label_key: c,
        });
      } catch (error) {
        console.error(`Failed to undo Absent for ${c}:`, error);
        failed.push(c);
      }
    }
    if (failed.length > 0) {
      setSaveError(
        `Undo incomplete: ${failed.length} of ${undoable.length} still Absent on the server ` +
          `(${failed.join(", ")}). Reload to see the recorded state.`,
      );
    }
    setBulkSaving(false);
  }, [bulkUndo, setLabelMap, caseId, deleteLabelMutation, projectId, caseLabels, combined]);

  // A bulk write is only undoable while the reader is still on that case.
  useEffect(() => {
    setBulkUndo(null);
  }, [caseId]);

  return (
    <div className="sidebar-premium sidebar-premium-border-l" data-testid="right-sidebar">
      {/* Subtle single-hue ambient wash */}
      <div className="absolute inset-0 bg-signal-ambient pointer-events-none" />

      {/* Header - compact */}
      <div className="relative px-3 py-2 shrink-0 space-y-1.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={onPrevious}
              disabled={!hasPrevious}
              className="sidebar-btn w-7 h-7"
              title="Previous (←)"
            >
              <FaChevronLeft className="w-3 h-3 text-fg-muted" />
            </button>

            <div className="text-center">
              <span className="text-lg font-bold tracking-tight text-white">
                {currentIndex + 1}
                <span className="text-fg-muted font-light text-sm">/{totalCases}</span>
              </span>
              <span
                className={`ml-2 text-[10px] ${isCaseComplete ? "text-signal-text-tint" : "text-fg-muted"}`}
              >
                {isCaseComplete ? "✓" : `${labeledCount}/${totalConditions}`}
              </span>
            </div>

            <button
              onClick={onNext}
              disabled={!hasNext}
              className="sidebar-btn w-7 h-7"
              title="Next (→)"
            >
              <FaChevronRight className="w-3 h-3 text-fg-muted" />
            </button>
          </div>

          <div className="flex items-center gap-1.5">
            {onWorkList && <WorkListButton onClick={onWorkList} />}
            {onNextUnlabeled && (
              <button
                onClick={onNextUnlabeled}
                disabled={!hasNextUnlabeled}
                className="sidebar-btn-primary h-7 px-3 flex items-center justify-center gap-1 text-[10px]"
                title="Next unlabeled (N)"
              >
                <span>Skip</span>
                <FaChevronRight className="w-2.5 h-2.5" />
              </button>
            )}
          </div>
        </div>

        {/* Progress - inline bar only */}
        {progressQuery.data && (
          <div className="flex items-center gap-2">
            <div className="sidebar-progress-track flex-1">
              <div
                className="sidebar-progress-fill"
                style={{
                  width: `${progressQuery.data.completion_percentage}%`,
                }}
              />
            </div>
            <span className="text-[10px] text-fg-muted tabular-nums">
              {progressQuery.data.completion_percentage.toFixed(0)}%
            </span>
          </div>
        )}

        {/* Quick action: mark every still-unlabeled finding Absent (Mike, #head-triage-26).
            Only fills unlabeled findings — positives already marked are untouched.

            Gated on `combined` so the plain single_select path (chest/renal/omni) stays
            byte-identical, per this file's own invariant — the head reader study is the
            requester and the only combined-mode project. Also gated on the case-labels
            fetch having SUCCEEDED: an empty caseLabels means "no labels" only after that,
            and acting on the pre-fetch state would bulk-Absent a case already filled in. */}
        {combined && unlabeledConditions.length > 0 && labelsLoaded && !isExamExcluded && (
          <button
            onClick={handleMarkRestAbsent}
            disabled={saveLabelMutation.isPending || bulkSaving || labelsFetching}
            className="w-full sidebar-btn flex items-center justify-center gap-1.5 h-7 text-[10px] text-fg-muted hover:text-white disabled:opacity-50"
            title="Set all remaining findings to Absent"
          >
            <FaTimes className="w-3 h-3" />
            <span>
              {bulkSaving ? "Saving…" : `Mark remaining ${unlabeledConditions.length} Absent`}
            </span>
          </button>
        )}
        {/* No silent fallback: if we can't read the existing labels we say so rather
            than offering a bulk action over an unknown state. */}
        {combined && labelsFailed && (
          <span className="block text-center text-[10px] text-danger-text-tint" role="status">
            Couldn't load existing labels — bulk actions unavailable
          </span>
        )}
        {combined && bulkUndo && bulkUndo.length > 0 && !bulkSaving && (
          <div className="flex items-center justify-center gap-2">
            <span className="text-[10px] text-fg-muted">Marked {bulkUndo.length} Absent</span>
            <button
              onClick={handleUndoMarkRestAbsent}
              className="text-[10px] text-fg-muted hover:text-fg underline underline-offset-2"
            >
              Undo
            </button>
          </div>
        )}
      </div>

      {/* Instructions button + dialog */}
      {instructionsKey && (
        <>
          <div className="px-3 pb-1 shrink-0">
            <button
              onClick={() => setShowInstructions(true)}
              className="w-full sidebar-btn flex items-center justify-center gap-1.5 h-7 text-[10px] text-fg-muted hover:text-white"
            >
              <FaFileAlt className="w-3 h-3" />
              <span>Instructions</span>
            </button>
          </div>
          <InstructionsDialog
            projectId={projectId}
            instructionsKey={instructionsKey}
            open={showInstructions}
            onClose={() => setShowInstructions(false)}
          />
        </>
      )}

      {/* Saving indicator */}
      {saveLabelMutation.isPending && (
        <div className="px-5 py-2.5 text-xs text-signal-text-tint text-center animate-pulse bg-primary/5 border-y border-primary/10">
          Saving...
        </div>
      )}

      {/* Rejected write — the answer was rolled back, so say so rather than leaving the reader
       *  believing a value that was never recorded. */}
      {saveError && (
        <div
          data-testid="labeling-save-error"
          role="alert"
          className="px-5 py-2.5 text-xs text-center bg-warn-tint border-y border-warn text-warn"
        >
          {saveError}
        </div>
      )}

      {/* Conditions list */}
      <div className="relative flex-1 overflow-y-auto px-3 py-2 min-h-0">
        <div className="space-y-1.5">
          {labelsToAssign.map((condition, idx) => {
            const labelInfo = caseLabels[condition];
            const parsedValue = combined ? parseFindingValue(labelInfo?.value) : null;
            const call = parsedValue?.call ?? parseCall(labelInfo?.value);
            const isLabeled = !!labelInfo;
            const isFocused = idx === effectiveFocusIndex;
            // An excluded exam takes no further answers (D12); the server rejects them too.
            const isLocked = isExamExcluded || (isLabeled && !isOwnLabel(labelInfo));
            const findingDone = isFindingComplete(
              labelInfo?.value,
              attributeDefinitions?.[condition],
              combined,
            );
            // Combined mode: reveal this finding's attributes inline when Present.
            const attrDefs = combined ? (attributeDefinitions?.[condition] ?? {}) : {};
            const attrNames = Object.keys(attrDefs);
            const attrs = parsedValue?.attributes ?? {};
            const showAttrs = combined && call === "Present" && attrNames.length > 0;

            return (
              <div key={condition} className="space-y-1">
                {/* Finding call card */}
                <div
                  data-testid={`labeling-condition-${idx}`}
                  data-active={isFocused ? "true" : undefined}
                  onClick={() => setFocusedIndex(idx)}
                  className={`sidebar-card rounded-lg flex items-center gap-2 px-3 py-2 cursor-pointer ${
                    isLocked
                      ? "opacity-40 cursor-not-allowed"
                      : isFocused
                        ? "sidebar-card-active ring-1 ring-primary/40"
                        : findingDone
                          ? "sidebar-card-completed opacity-60"
                          : ""
                  }`}
                >
                  {/* Condition name */}
                  <span
                    className={`flex-1 text-xs font-medium leading-tight text-white/90 min-w-0 ${findingDone ? "opacity-60" : ""}`}
                  >
                    {isLocked && <FaLock className="w-2.5 h-2.5 inline mr-1 text-fg-muted" />}
                    {condition}
                  </span>

                  {/* Compact inline buttons — one per label_schema option (binary by default) */}
                  <div className="flex gap-1.5 shrink-0">
                    {optionsFor(condition).map((opt) => (
                      <button
                        key={opt}
                        data-testid={`labeling-call-${idx}-${opt.replace(/\s+/g, "-")}`}
                        onClick={() => handleLabelSelect(condition, opt)}
                        disabled={isLocked}
                        // Toggle button: selection was conveyed by fill colour alone, which
                        // is neither announced nor assertable.
                        aria-pressed={call === opt}
                        className={`w-7 h-7 rounded-lg text-xs font-semibold transition-all duration-150 flex items-center justify-center ${
                          call === opt
                            ? "bg-signal text-white shadow-xs shadow-signal/30"
                            : "sidebar-btn hover:border-signal-stroke-soft"
                        } disabled:opacity-30 disabled:cursor-not-allowed`}
                        title={
                          isLocked
                            ? `Labeled by ${formatLabeledBy(labelInfo?.labeled_by || "")}`
                            : `${opt} (click again to undo)`
                        }
                      >
                        {opt === "Present" ? (
                          <FaCheck className="w-3 h-3" />
                        ) : opt === "Absent" ? (
                          <FaTimes className="w-3 h-3" />
                        ) : (
                          <span className="text-[9px] font-semibold leading-none">
                            {optionAbbrev(opt)}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {parsedValue?.error && (
                  <div
                    role="alert"
                    data-testid={`labeling-attribute-parse-error-${idx}`}
                    className="ml-2 rounded-lg border border-warn/40 bg-warn-tint px-3 py-2 text-[11px] text-warn"
                  >
                    Saved attributes could not be read. Select Present to replace this label before
                    completing the case.
                  </div>
                )}

                {/* Inline attributes — revealed + required when the finding is Present */}
                {showAttrs && (
                  <div
                    data-testid={`labeling-attributes-${idx}`}
                    className={`ml-2 rounded-lg border px-3 py-2 space-y-2.5 ${
                      findingDone
                        ? "border-signal-stroke bg-signal-tint"
                        : "border-primary/30 bg-primary/5"
                    }`}
                  >
                    {attrNames.map((attrName) => {
                      const opts = attrDefs[attrName] ?? [];
                      const selected = attrs[attrName] ?? [];
                      const filled = selected.length > 0;
                      return (
                        <div key={attrName}>
                          <div className="text-[10px] mb-1 flex items-center gap-1.5">
                            <span className={filled ? "text-fg-muted" : "text-primary"}>
                              {attrName}
                            </span>
                            {!filled && (
                              <span className="text-primary text-[9px] tracking-wide">
                                • required
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {opts.map((opt) => {
                              const checked = selected.includes(opt);
                              return (
                                <button
                                  key={opt}
                                  data-testid={`labeling-attr-${idx}-${attrName.replace(/\s+/g, "-")}-${opt.replace(/\s+/g, "-")}`}
                                  disabled={isLocked}
                                  onClick={() => handleAttrToggle(condition, attrName, opt)}
                                  className={`px-2 py-0.5 rounded text-[10px] border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                                    checked
                                      ? "bg-signal-tint border-signal-stroke text-signal-text-tint"
                                      : "bg-surface-2 border-border text-fg-muted hover:border-border-strong hover:text-fg"
                                  }`}
                                >
                                  {opt}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <UnassessableCaseControl
        projectId={projectId}
        studyInstanceUid={studyInstanceUid}
        dispositions={currentCaseStatus?.unassessable_dispositions}
        disabled={bulkSaving || markCompleteMutation.isPending}
        examDisposition={examDisposition}
        examExcluded={isExamExcluded}
        technicalHolds={currentCaseStatus?.technical_holds}
        onExcluded={handleAdvance}
      />

      {/* Mark Complete — sticky footer (shrink-0), NOT inside the scroll area.
          Previously this lived at the bottom of the scrolling conditions list, so
          marking findings Present and expanding their inline attributes pushed it
          below the fold and readers "lost" it (Mike, #head-triage-26). As a footer
          it stays pinned and visible no matter how tall the attribute list grows. */}
      {isCaseComplete && !isCaseUnassessable && !isExamExcluded && (
        // `relative` so the footer paints above the absolute ambient wash, matching
        // the header and the conditions list.
        <div className="relative px-3 pb-3 pt-2 border-t border-border shrink-0 space-y-2">
          {showCompleteConfirm ? (
            <div className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-signal-tint border border-signal-stroke">
              <FaCheck className="w-3 h-3 text-signal-text-tint" />
              <span className="text-xs text-signal-text-tint font-medium">Marked complete</span>
              <button
                onClick={handleUndoComplete}
                className="text-[10px] text-fg-muted hover:text-fg underline underline-offset-2 ml-1"
              >
                Undo
              </button>
            </div>
          ) : (
            <button
              onClick={handleMarkComplete}
              disabled={markCompleteMutation.isPending || bulkSaving}
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg
                bg-signal-tint border border-signal-stroke text-signal-text-tint
                hover:bg-signal/20 hover:border-signal/50
                disabled:opacity-50 transition-all duration-200"
            >
              <FaCheck className="w-3 h-3" />
              <span className="text-xs font-medium">
                {markCompleteMutation.isPending
                  ? "Marking..."
                  : bulkSaving
                    ? "Saving answers…"
                    : "Mark Complete"}
              </span>
            </button>
          )}
          {!showCompleteConfirm && (
            <div className="flex justify-center">
              <span className="text-[10px] text-fg-muted">→ or N to continue without marking</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
